import React, { Suspense, Fragment, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { SET_AXIOS } from './actions/actions';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createBaseAxiosInstance } from './axios-base';

import './App.css';
import { Route, Routes, useLocation } from 'react-router-dom';
import {
    setExpirationDate,
    setIsOpened,
} from './are-you-still-here/are-you-still-here-slice/are-you-still-here-slice';
import { TokenHelper } from './utils/tokenHelper';
import { useStateSelector } from './store/selectors';
import DefaultMeta from './components/DefaultMeta/DefaultMeta';
import { RefreshNotificationFlags } from './slices/notification-flags/refresh-notification-flags';
import { NotificationFlagsProvider } from './components/NotificationFlagsProvider/NotificationFlagsProvider';
import { FetchServiceFocuses } from './slices/service-focuses/thunks/fetch-service-focuses.thunk';
import { RequireAuth } from './routing/guards/RequireAuth';
import { AppLoader } from './routing/AppLoader';
import { PublicShell } from './routing/shells/PublicShell';
import { AppShell } from './routing/shells/AppShell';
import { BuiltInShell } from './routing/shells/BuiltInShell';
import { VenueShell } from './routing/shells/VenueShell';
import { RequireVenueAccess } from './routing/guards/RequireVenueAccess';
import { renderRoutes } from './routing/render-routes';
import { PUBLIC_ROUTES } from './routing/areas/public.routes';
import { BUILT_IN_ROUTES } from './routing/areas/built-in.routes';
import { ACCOUNT_ROUTES } from './routing/areas/account.routes';
import { ADMIN_ROUTES } from './routing/areas/admin.routes';
import { REPORT_ROUTES } from './routing/areas/report.routes';
import { LEGACY_FLAT_ROUTES } from './routing/areas/legacy-flat.routes';
import { GROW_ROUTES } from './routing/areas/grow.routes';
import { ALLY_ROUTES } from './routing/areas/ally.routes';
import { CONNECT_ROUTES } from './routing/areas/connect.routes';
import { SHARE_ROUTES } from './routing/areas/share.routes';
import { LEGACY_REDIRECTS } from './routing/legacy-redirects';
import { LegacyRedirect } from './routing/LegacyRedirect';
import { NotFound } from './routing/NotFound';
import { VenueId } from './venues/venues.types';
import { paths } from './routing/path';

export const App = () => {
    const dispatch = useDispatch() as any;
    const userActivityEvents = ['keydown', 'mousedown', 'click', 'scroll'];
    const delayToRefreshIfUserInactive = 10000;
    const delayToRefreshIfUserActive = 120000;
    const isOpened = useStateSelector(
        (state) => state.areYouStillHere.isOpened
    );
    const axios = useStateSelector((state) => state.core.axios);
    const location = useLocation();

    useEffect(() => {
        const initiate = async () => {
            await dispatch({
                type: SET_AXIOS,
                payload: createBaseAxiosInstance(),
            });
            if (
                !(
                    location.pathname === paths.public.register &&
                    location.search.startsWith('?user=')
                )
            ) {
                await dispatch(RefreshNotificationFlags());
            }
            refreshToken();
        };
        initiate();
    }, []);

    useEffect(() => {
        dispatch(FetchServiceFocuses());
    }, [dispatch]);

    const debounce = (func: () => void, delay: number) => {
        let timeoutId: ReturnType<typeof setTimeout>;
        return function () {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func();
            }, delay);
        };
    };

    useEffect(() => {
        let isUserActive = false;
        let refreshTokenTimer: ReturnType<typeof setTimeout>;

        const delayedRefreshToken = debounce(() => {
            refreshToken();
        }, delayToRefreshIfUserInactive);

        const activityEventsListener = () => {
            isUserActive = true;
            clearTimeout(refreshTokenTimer);
            refreshTokenTimer = setTimeout(() => {
                isUserActive = false;
                delayedRefreshToken();
            }, delayToRefreshIfUserInactive);
        };

        userActivityEvents.forEach((ev) =>
            document.addEventListener(ev, activityEventsListener, false)
        );

        const intervalId = setInterval(() => {
            if (isUserActive) {
                refreshToken();
            }
        }, delayToRefreshIfUserActive);

        return () => {
            userActivityEvents.forEach((ev) =>
                document.removeEventListener(ev, activityEventsListener, false)
            );
            clearInterval(intervalId);
            clearTimeout(refreshTokenTimer);
        };
    }, [isOpened]);

    const refreshToken = () => {
        if (!isOpened) {
            const storedRefreshToken = localStorage.getItem('refreshToken');
            const deviceKey = localStorage.getItem('deviceKey');
            const accessToken = localStorage.getItem('accessToken');
            const deviceGroupKey = localStorage.getItem('deviceGroupKey');
            const token = localStorage.getItem('token');

            if (storedRefreshToken) {
                axios
                    .post(
                        `/api/auth/refreshUserToken`,
                        {
                            refreshToken: storedRefreshToken,
                            deviceKey: deviceKey,
                            accessToken: accessToken,
                            deviceGroupKey: deviceGroupKey,
                        },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: 'Bearer ' + token,
                            },
                        }
                    )
                    .then((response: any) => {
                        const authenticationResult = response?.data?.data;
                        if (authenticationResult) {
                            TokenHelper.setTokenToLocalStorage(
                                authenticationResult
                            );
                            let expirationDate = new Date();
                            expirationDate.setSeconds(
                                expirationDate.getSeconds() +
                                    authenticationResult.expiresIn
                            );
                            dispatch(setExpirationDate(expirationDate));
                        }
                        dispatch(setIsOpened(false));
                    });
            }
        }
    };

    return (
        <Fragment>
            <DefaultMeta />
            <NotificationFlagsProvider />
            <Suspense fallback={<AppLoader />}>
                <Routes>
                    <Route element={<PublicShell />}>
                        {renderRoutes(PUBLIC_ROUTES)}
                    </Route>

                    <Route element={<RequireAuth />}>
                        <Route element={<BuiltInShell />}>
                            {renderRoutes(BUILT_IN_ROUTES)}
                        </Route>

                        <Route path={paths.venues.grow.root} element={<RequireVenueAccess venueId={VenueId.Grow} />}>
                            <Route element={<VenueShell venueId={VenueId.Grow} />}>
                                {renderRoutes(GROW_ROUTES.filter(r => !r.withAppShell))}
                                <Route element={<AppShell />}>
                                    {renderRoutes(GROW_ROUTES.filter(r => r.withAppShell))}
                                </Route>
                            </Route>
                        </Route>
                        <Route path={paths.venues.ally.root} element={<RequireVenueAccess venueId={VenueId.Ally} />}>
                            <Route element={<VenueShell venueId={VenueId.Ally} />}>
                                {renderRoutes(ALLY_ROUTES.filter(r => !r.withAppShell))}
                                <Route element={<AppShell />}>
                                    {renderRoutes(ALLY_ROUTES.filter(r => r.withAppShell))}
                                </Route>
                            </Route>
                        </Route>
                        <Route path={paths.venues.connect.root} element={<RequireVenueAccess venueId={VenueId.Connect} />}>
                            <Route element={<VenueShell venueId={VenueId.Connect} />}>
                                {renderRoutes(CONNECT_ROUTES.filter(r => !r.withAppShell))}
                                <Route element={<AppShell />}>
                                    {renderRoutes(CONNECT_ROUTES.filter(r => r.withAppShell))}
                                </Route>
                            </Route>
                        </Route>
                        <Route path={paths.venues.share.root} element={<RequireVenueAccess venueId={VenueId.Share} />}>
                            <Route element={<VenueShell venueId={VenueId.Share} />}>
                                {renderRoutes(SHARE_ROUTES.filter(r => !r.withAppShell))}
                                <Route element={<AppShell />}>
                                    {renderRoutes(SHARE_ROUTES.filter(r => r.withAppShell))}
                                </Route>
                            </Route>
                        </Route>

                        {renderRoutes(ACCOUNT_ROUTES.filter(r => !r.withAppShell))}
                        {renderRoutes(ADMIN_ROUTES.filter(r => !r.withAppShell))}
                        {renderRoutes(REPORT_ROUTES.filter(r => !r.withAppShell))}
                        {renderRoutes(LEGACY_FLAT_ROUTES.filter(r => !r.withAppShell))}

                        <Route element={<AppShell />}>
                            {renderRoutes(ACCOUNT_ROUTES.filter(r => r.withAppShell))}
                            {renderRoutes(ADMIN_ROUTES.filter(r => r.withAppShell))}
                            {renderRoutes(REPORT_ROUTES.filter(r => r.withAppShell))}
                            {renderRoutes(LEGACY_FLAT_ROUTES.filter(r => r.withAppShell))}
                        </Route>

                        {LEGACY_REDIRECTS.map((r) => (
                            <Route key={r.from} path={r.from} element={<LegacyRedirect to={r.to} />} />
                        ))}
                    </Route>

                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
            <ToastContainer theme="colored" />
        </Fragment>
    );
};

export default App;
